/**
 * Esperar a que una regeneración termine de verdad antes de decir que terminó.
 *
 * `POST /interpretations/{id}/regenerate` sólo ARRANCA la máquina de estados: devuelve de
 * inmediato y el texto nuevo tarda ~80 s en escribirse (el panel se reparte en áreas, se
 * interpreta cada una y se consolidan). El detalle consultaba la interpretación en el
 * instante siguiente —recibiendo el texto VIEJO y el estado `processing`— y aun así
 * anunciaba «la nueva interpretación está lista para revisión». El médico leía que había
 * acabado, veía el texto de antes, y sólo una recarga forzada se lo mostraba.
 *
 * Un aviso que miente es peor que no avisar: quien lo lee concluye que la regeneración
 * falló. Esto sondea hasta que el registro cambia de verdad y deja que la pantalla refleje
 * cada paso, para que «procesando» sea cierto mientras se muestra.
 *
 * El criterio de «ya está» es `regeneration_count`, no el estado: es un contador que sólo
 * avanza cuando el registro se reescribió. Mirar `status` solo no basta — vuelve a
 * `pending_review`, que es de donde salió, así que un sondeo que llegue tarde o temprano
 * no podría distinguir «ya terminó» de «aún no ha empezado».
 */

export type ResultadoRegeneracion = 'ready' | 'failed' | 'timeout';

export interface InterpretacionSondeable {
  status?: string;
  regeneration_count?: number;
}

export interface OpcionesEspera<T extends InterpretacionSondeable> {
  obtener: (id: string) => Promise<T>;
  /** Pausa entre sondeos. */
  intervaloMs?: number;
  /** Cuánto se espera antes de rendirse y decirlo. */
  limiteMs?: number;
  dormir?: (ms: number) => Promise<void>;
  ahora?: () => number;
}

const dormirPorDefecto = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function esperarRegeneracion<T extends InterpretacionSondeable>(
  id: string,
  conteoPrevio: number,
  alActualizar: (dato: T) => void,
  opciones: OpcionesEspera<T>,
): Promise<ResultadoRegeneracion> {
  const {
    obtener,
    intervaloMs = 4000,
    limiteMs = 240000,
    dormir = dormirPorDefecto,
    ahora = () => Date.now(),
  } = opciones;

  const inicio = ahora();

  while (ahora() - inicio < limiteMs) {
    await dormir(intervaloMs);

    let dato: T;
    try {
      dato = await obtener(id);
    } catch {
      // Un fallo de red durante la espera no es un fallo de la regeneración: se reintenta
      // en el siguiente sondeo. Rendirse aquí dejaría al médico con el texto viejo y un
      // error que no describe lo que pasó.
      continue;
    }

    alActualizar(dato);

    if (dato.status === 'error') return 'failed';
    if (Number(dato.regeneration_count ?? 0) > conteoPrevio && dato.status !== 'processing') {
      return 'ready';
    }
  }

  return 'timeout';
}
